/**
 * Campaign Form Hook
 * 
 * Centralized form state management for campaign creation/editing:
 * - Form data state and validation
 * - Step navigation and validation
 * - Draft saving and campaign submission
 * - API error handling
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  CampaignFormData, 
  CampaignValidationErrors, 
  CampaignStepConfig,
  FundingType 
} from '../../../../../api/promos/types';
import { createCampaign, saveCampaignAsDraft, updateCampaign } from '../../../../../api/promos';

interface UseCampaignFormReturn {
  // State
  currentStep: number;
  formData: CampaignFormData;
  validationErrors: CampaignValidationErrors;
  isDraft: boolean;
  isSubmitting: boolean;
  steps: CampaignStepConfig[];
  
  // Navigation
  goToStep: (stepIndex: number) => void;
  validateStep: (stepIndex: number) => boolean;
  
  // Form management
  handleFormChange: (updates: Partial<CampaignFormData>) => void;
  
  // Submission
  saveDraft: () => Promise<void>;
  submitCampaign: () => Promise<void>;
  
  // Special actions
  skipStep: (stepIndex: number) => void;
}

export const useCampaignForm = (
  initialData: CampaignFormData,
  campaignId?: string,
  isEditMode: boolean = false
): UseCampaignFormReturn => {
  // Form state
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<CampaignFormData>(initialData);
  const [validationErrors, setValidationErrors] = useState<CampaignValidationErrors>({});
  const [isDraft, setIsDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skippedSteps, setSkippedSteps] = useState<number[]>([]);
  
  // Update formData when initialData changes (for edit mode)
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);
  
  // Validation functions
  const validateStepData = useCallback((stepIndex: number): boolean => {
    const errors: CampaignValidationErrors = {};
    
    switch (stepIndex) {
      case 0: // Basic Information
        if (!formData.name?.trim()) {
          errors.name = 'Campaign name is required';
        } else if (formData.name.trim().length < 3) {
          errors.name = 'Campaign name must be at least 3 characters';
        }
        
        if (!formData.campaignType) {
          errors.campaignType = 'Campaign type is required';
        }
        
        if (!formData.startAt) {
          errors.startAt = 'Start date is required';
        }
        
        if (!formData.endAt) {
          errors.endAt = 'End date is required';
        } else if (formData.startAt && new Date(formData.endAt) < new Date(formData.startAt)) {
          errors.endAt = 'Expiry date must be after valid from date';
        } else if (formData.startAt && new Date(formData.endAt).getTime() === new Date(formData.startAt).getTime()) {
          errors.endAt = 'Expiry date must be after valid from date';
        }
        
        break;
        
      case 1: // Link Promos & Funding (optional)
        // This step is optional, so no required field validation
        // Only validate if user has entered data
        if (formData.funding) {
          if (!formData.funding.fundedBy) {
            if (!errors.funding) errors.funding = {};
            errors.funding.fundedBy = 'Funding type is required';
          }
          
          if (formData.funding.fundedBy === FundingType.SHARED) {
            if (!formData.funding.platformShare || formData.funding.platformShare < 0 || formData.funding.platformShare > 100) {
              if (!errors.funding) errors.funding = {};
              errors.funding.platformShare = 'Platform share must be between 0 and 100';
            }
          }
        }
        break;
        
      case 2: // Summary
        // Final validation - all previous steps must be valid
        const step0Valid = validateStepData(0);
        const step1Valid = skippedSteps.includes(1) || validateStepData(1);
        return step0Valid && step1Valid;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, skippedSteps]);

  // Step configuration
  const steps: CampaignStepConfig[] = useMemo(() => [
    {
      id: 'basic-info',
      title: 'Basic Information',
      isCompleted: currentStep > 0 && validateStepData(0),
      isActive: currentStep === 0,
    },
    {
      id: 'link-promos',
      title: 'Link Promo & Funding',
      isCompleted: currentStep > 1 && (validateStepData(1) || skippedSteps.includes(1)),
      isActive: currentStep === 1,
      isOptional: true,
    },
    {
      id: 'summary',
      title: 'Summary',
      isCompleted: currentStep > 2,
      isActive: currentStep === 2,
    },
  ], [currentStep, skippedSteps, validateStepData]);
  
  // Navigation functions
  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
      // Clear validation errors when changing steps
      setValidationErrors({});
    }
  }, [steps.length]);
  
  const validateStep = useCallback((stepIndex: number) => {
    return validateStepData(stepIndex);
  }, [validateStepData]);
  
  // Form management
  const handleFormChange = useCallback((updates: Partial<CampaignFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    // Clear related validation errors
    if (updates.name) {
      setValidationErrors(prev => ({ ...prev, name: undefined }));
    }
    if (updates.campaignType) {
      setValidationErrors(prev => ({ ...prev, campaignType: undefined }));
    }
    if (updates.startAt) {
      setValidationErrors(prev => ({ ...prev, startAt: undefined }));
    }
    if (updates.endAt) {
      setValidationErrors(prev => ({ ...prev, endAt: undefined }));
    }
    if (updates.funding) {
      setValidationErrors(prev => ({ ...prev, funding: undefined }));
    }
  }, []);
  
  // Skip step function
  const skipStep = useCallback((stepIndex: number) => {
    setSkippedSteps(prev => [...prev, stepIndex]);
  }, []);
  
  // Save as draft
  const saveDraft = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setIsDraft(true);
      
      // Basic validation for draft (only required fields from step 1)
      if (!formData.name?.trim()) {
        throw new Error('Campaign name is required to save as draft');
      }
      
      if (isEditMode && campaignId) {
        // Update existing campaign
        await updateCampaign(campaignId, formData);
      } else {
        // Create new campaign with DRAFT status
        await saveCampaignAsDraft(formData);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isEditMode, campaignId]);
  
  // Submit campaign (activate)
  const submitCampaign = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setIsDraft(false);
      
      // Validate all required steps
      const allStepsValid = validateStepData(0) && (skippedSteps.includes(1) || validateStepData(1));
      
      if (!allStepsValid) {
        throw new Error('Please complete all required fields before activating');
      }
      
      if (isEditMode && campaignId) {
        // Update existing campaign
        await updateCampaign(campaignId, formData);
      } else {
        // Create new campaign with ACTIVE status
        await createCampaign(formData, 'ACTIVE');
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, skippedSteps, validateStepData, isEditMode, campaignId]);
  
  return {
    // State
    currentStep,
    formData,
    validationErrors,
    isDraft,
    isSubmitting,
    steps,
    
    // Navigation
    goToStep,
    validateStep,
    
    // Form management
    handleFormChange,
    
    // Submission
    saveDraft,
    submitCampaign,
    
    // Special actions
    skipStep,
  };
};